/* =====================================================================
   TPS delivery pincode — shared memory + collection-page timelines

   TWO JOBS:

   1. ONE REMEMBERED PINCODE ACROSS THE SITE.
      The shopper is asked at most once. Whatever pincode is in play —
      auto-detected on first visit, or typed by the shopper on either the
      product page or a collection page — is stored and reused everywhere
      until they change it. A pincode the shopper typed themselves is
      marked as such and is never overwritten by auto-detection again.

   2. DELIVERY TIMELINES ON COLLECTION ROWS.
      Shows "FREE delivery by <date>" per product using the same APIs the
      product page uses.

   WHY THIS FILE RATHER THAN CHANGING getPincodeDetails():
   that function in tpscustomjs.js is hard-wired to one product's DOM — it
   reads #textpincodevalue, takes the product id from #pincodeContainer's
   title, and writes into #ResponsePin. All singular ids, so it cannot
   drive 20-50 rows. The BUSINESS RULES are not duplicated here:
   getNextWorkingDate, filterCourierDays, isHeavyWeightPincodeAvailable,
   locationDistrictMap and locally_available_warehouse_pincode are globals
   from tpscustomjs.js and are reused directly, so cutoff times, holidays,
   Sundays and heavy-item rules stay defined in exactly one place.

   PERFORMANCE: each product needs TWO sequential API calls, so a
   54-product page would be 108 requests if done naively. Instead only
   rows scrolled into view are fetched, at most 2 at a time, cached per
   pincode+product, and nothing runs until a pincode is known.

   LOAD ORDER: this file is loaded from <head> in layout/theme.liquid with
   defer, so it executes BEFORE the deferred tpscustomjs.js in the footer.
   That ordering matters for the geolocation guard installed below.
   ===================================================================== */

(function () {
  if (window.__tpsPincode) return;
  window.__tpsPincode = true;

  /* Log recently-viewed products (powers the empty-cart experience). Runs on
     every page; only acts on product pages, storing product handles locally. */
  (function () {
    var m = location.pathname.match(/\/products\/([^/?#]+)/);
    if (!m) return;
    var handle = m[1];
    try {
      var RV_KEY = 'tps_recently_viewed';
      var list = JSON.parse(localStorage.getItem(RV_KEY) || '[]');
      list = list.filter(function (h) { return h !== handle; });
      list.unshift(handle);
      localStorage.setItem(RV_KEY, JSON.stringify(list.slice(0, 12)));
    } catch (e) {}
  })();

  var PIN_KEY = 'tps_pincode';
  var PIN_SRC_KEY = 'tps_pincode_source'; /* 'user' | 'auto' */
  var CACHE_PREFIX = 'tps_etd:';
  var MAX_CONCURRENT = 2;
  var FALLBACK_PIN = '560094';

  var queue = [];
  var active = 0;
  var observer = null;

  /* ---------------- Stored pincode ---------------- */

  function getPin() {
    try {
      return localStorage.getItem(PIN_KEY) || '';
    } catch (e) {
      return '';
    }
  }

  function getSource() {
    try {
      return localStorage.getItem(PIN_SRC_KEY) || '';
    } catch (e) {
      return '';
    }
  }

  function savePin(pin, source) {
    if (!/^\d{6}$/.test(pin)) return false;
    /* Never let auto-detection clobber a pincode the shopper chose. */
    if (source === 'auto' && getSource() === 'user') return false;
    try {
      localStorage.setItem(PIN_KEY, pin);
      localStorage.setItem(PIN_SRC_KEY, source);
    } catch (e) {
      /* private mode — in-memory for this page only */
    }
    return true;
  }

  /* ---------------- Geolocation guard ----------------
     tpscustomjs.js calls getPostcodeAddessLocation() on every product
     page, which fires navigator.geolocation.getCurrentPosition() and then
     overwrites the pincode field with whatever it resolves. That would
     re-prompt the shopper and discard a pincode they had already chosen.

     Since this file runs first, the API is wrapped so that when a pincode
     is already known, the position callback is skipped entirely — no
     prompt, no overwrite. When nothing is stored yet, the original
     behaviour runs untouched and the result gets saved for reuse. The
     wrapper is removed once it has served its purpose. */
  if (navigator.geolocation && typeof navigator.geolocation.getCurrentPosition === 'function') {
    var nativeGetPosition = navigator.geolocation.getCurrentPosition.bind(navigator.geolocation);

    navigator.geolocation.getCurrentPosition = function (success, error, options) {
      if (getPin().length === 6) {
        /* Already know where they are. Skip the prompt and let the page
           fill itself in from storage instead. */
        return;
      }
      return nativeGetPosition(success, error, options);
    };
  }

  /* ---------------- Shared helpers ---------------- */

  function fmtDate(d) {
    return days[d.getDay()] + ', ' + months[d.getMonth()] + ' ' + d.getDate();
  }

  function cacheGet(pin, id) {
    try {
      return sessionStorage.getItem(CACHE_PREFIX + pin + ':' + id);
    } catch (e) {
      return null;
    }
  }

  function cacheSet(pin, id, val) {
    try {
      sessionStorage.setItem(CACHE_PREFIX + pin + ':' + id, val);
    } catch (e) {
      /* quota — skip caching */
    }
  }

  /* Detect a pincode from the browser's location, once, if we have none.
     Mirrors getPostcodeAddessLocation() in tpscustomjs.js. */
  function autoDetectPin() {
    return new Promise(function (resolve) {
      if (getPin().length === 6) return resolve(getPin());
      if (!navigator.geolocation) return resolve('');

      var done = false;
      var timer = setTimeout(function () {
        if (!done) {
          done = true;
          resolve('');
        }
      }, 8000);

      nativeGetPosition(
        async function (pos) {
          if (done) return;
          try {
            var res = await fetch(
              'https://nominatim.openstreetmap.org/reverse?format=json&lat=' +
                pos.coords.latitude +
                '&lon=' +
                pos.coords.longitude
            );
            var data = await res.json();
            var pin = data && data.address && data.address.postcode;
            pin = pin && /^\d{6}$/.test(pin) ? pin : FALLBACK_PIN;
            savePin(pin, 'auto');
            done = true;
            clearTimeout(timer);
            resolve(pin);
          } catch (e) {
            savePin(FALLBACK_PIN, 'auto');
            done = true;
            clearTimeout(timer);
            resolve(FALLBACK_PIN);
          }
        },
        function () {
          if (done) return;
          done = true;
          clearTimeout(timer);
          resolve('');
        },
        { timeout: 7000, maximumAge: 600000 }
      );
    });
  }

  /* ---------------- Product page sync ----------------
     Keeps the product page's own pincode tool and this shared memory in
     step, in both directions, without modifying tpscustomjs.js. */
  function syncProductPage() {
    var input = document.getElementById('textpincodevalue');
    if (!input) return;

    var stored = getPin();

    /* Storage -> field: prefill and run the existing tool once. */
    if (stored.length === 6 && input.value !== stored) {
      input.value = stored;
      if (typeof getPincodeDetails === 'function') {
        try {
          getPincodeDetails('shared');
        } catch (e) {
          /* fail silently — the tool shows its own error state */
        }
      }
    }

    /* Field -> storage: anything the shopper types wins from then on. */
    function capture() {
      var val = (input.value || '').replace(/\D/g, '').slice(0, 6);
      if (val.length === 6) savePin(val, 'user');
    }

    input.addEventListener('change', capture);
    input.addEventListener('blur', capture);

    var btn = document.getElementById('pincode');
    if (btn) btn.addEventListener('click', capture);

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') capture();
    });
  }

  /* ---------------- Collection page: per-row ETD ---------------- */

  async function fetchEtd(pin, productId) {
    var r1 = await fetch(
      '/apps/trackorder/checkProductAvailableLocation?q=' +
        encodeURIComponent(pin) +
        '&productId=' +
        encodeURIComponent(productId) +
        '&lst=' +
        locally_available_warehouse_pincode
    );
    if (!r1.ok) throw new Error('availability lookup failed');
    var data = await r1.json();

    var holidays = (data.pickuppincode_details && data.pickuppincode_details.holiday_list) || [];
    var next = getNextWorkingDate(holidays, '14:00');
    var additionalDays = next.additionalDays;

    var inv = ((data.inventory && data.inventory.Inventory) || []).filter(function (i) {
      return i.available > 0;
    });
    if (!inv.length) return '';

    var warehouseMap = {};
    (data.wh || []).forEach(function (wh) {
      warehouseMap[wh.Pincode] = {
        district: (wh.District || '').toUpperCase(),
        state: (wh.State || '').toUpperCase()
      };
    });

    var district = ((data.pickuppincode_details && data.pickuppincode_details.District) || '').toUpperCase();
    var state = ((data.pickuppincode_details && data.pickuppincode_details.State) || '').toUpperCase();

    var isLocal = inv.some(function (item) {
      var loc = locationDistrictMap[item.location_id];
      if (!loc) return false;
      var wh = warehouseMap[loc.pincode];
      return wh && (wh.district === district || (state === 'DELHI' && wh.state === 'DELHI'));
    });

    if (isLocal) {
      if (additionalDays === 0) return 'Local delivery by <b>today 9 PM</b>';
      if (additionalDays === 1) return 'Local delivery by <b>tomorrow</b>';
      return 'Local delivery by <b>' + fmtDate(new Date(next.nextDate)) + '</b>';
    }

    var nearest = FALLBACK_PIN;
    var minDist = Infinity;
    inv.forEach(function (item) {
      var loc = locationDistrictMap[item.location_id];
      if (loc && loc.pincode) {
        var dist = Math.abs(Number(loc.pincode) - Number(data.pickuppincode_details.Pincode));
        if (dist < minDist) {
          minDist = dist;
          nearest = loc.pincode;
        }
      }
    });

    var weight = (data.inventory && data.inventory.ProductWeight) || 1.5;
    var type = (data.inventory && data.inventory.ProductType) || '';

    var heavyTypes = ['Monitor', 'Laptop Battery', 'Laptop', 'Desktop PC'];
    var isHeavyPin = false;
    try {
      isHeavyPin = await isHeavyWeightPincodeAvailable(pin);
    } catch (e) {
      isHeavyPin = false;
    }

    if (isHeavyPin && heavyTypes.indexOf(type) !== -1) {
      var hd = new Date(next.nextDate);
      hd.setDate(hd.getDate() + 3);
      return 'Delivery by <b>' + fmtDate(hd) + '</b>';
    }

    var r2 = await fetch(
      '/apps/trackorder/checkShiprocketDetailsforETD?q=' +
        encodeURIComponent(nearest) +
        '&pincode=' +
        encodeURIComponent(data.pickuppincode_details.Pincode) +
        '&weight=' +
        encodeURIComponent(weight)
    );
    if (!r2.ok) throw new Error('ETD lookup failed');
    var data2 = await r2.json();

    var couriers = filterCourierDays(data2.data['available_courier_companies'], type);
    if (!couriers) return '';

    /* Standard (mode 0) is the free-shipping promise — the one worth
       showing in a listing. Express is a product-page detail. */
    var m0 = couriers.mode0;
    if (!m0 || (!m0.min && !m0.minDate)) return '';

    var d0;
    if (m0.min) {
      d0 = new Date();
      d0.setDate(d0.getDate() + m0.min + additionalDays);
    } else {
      d0 = new Date(m0.minDate);
      d0.setDate(d0.getDate() + additionalDays);
    }

    return 'FREE delivery by <b>' + fmtDate(d0) + '</b>';
  }

  function render(el, html) {
    if (!html) {
      el.hidden = true;
      el.innerHTML = '';
      return;
    }
    el.hidden = false;
    el.innerHTML = html;
  }

  function pump() {
    while (active < MAX_CONCURRENT && queue.length) {
      var job = queue.shift();
      active++;
      (function (j) {
        var pin = getPin();
        var cached = cacheGet(pin, j.id);
        if (cached !== null) {
          render(j.el, cached);
          active--;
          pump();
          return;
        }
        fetchEtd(pin, j.id)
          .then(function (msg) {
            cacheSet(pin, j.id, msg);
            render(j.el, msg);
          })
          .catch(function () {
            render(j.el, '');
          })
          .then(function () {
            active--;
            pump();
          });
      })(job);
    }
  }

  function enqueue(row) {
    var el = row.querySelector('.tps-plp__delivery');
    var id = row.getAttribute('data-product-id');
    if (!el || !id || el.dataset.done === '1') return;
    el.dataset.done = '1';
    el.hidden = false;
    el.innerHTML = '<span class="tps-plp__delivery-loading">Checking delivery…</span>';
    queue.push({ el: el, id: id });
    pump();
  }

  function observeRows() {
    var rows = document.querySelectorAll('.tps-plp[data-product-id]');
    if (!rows.length) return;

    if (!('IntersectionObserver' in window)) {
      Array.prototype.slice.call(rows, 0, 6).forEach(enqueue);
      return;
    }

    if (observer) observer.disconnect();
    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            enqueue(entry.target);
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '200px 0px' }
    );

    rows.forEach(function (row) {
      observer.observe(row);
    });
  }

  function resetRows() {
    document.querySelectorAll('.tps-plp__delivery').forEach(function (el) {
      el.dataset.done = '';
      el.hidden = true;
      el.innerHTML = '';
    });
    queue.length = 0;
  }

  function setBarState(bar) {
    var input = bar.querySelector('.tps-plp-deliverybar__input');
    if (input) input.value = getPin();
  }

  function buildBar() {
    var host = document.querySelector('.tps-amz-cards .product-grid-container');
    if (!host || document.querySelector('.tps-plp-deliverybar')) return null;

    var bar = document.createElement('div');
    bar.className = 'tps-plp-deliverybar';
    bar.innerHTML =
      '<span class="tps-plp-deliverybar__label">Deliver to</span>' +
      '<input class="tps-plp-deliverybar__input" type="text" inputmode="numeric" maxlength="6" ' +
      'placeholder="Pincode" aria-label="Delivery pincode" value="' +
      getPin() +
      '">' +
      '<button class="tps-plp-deliverybar__btn" type="button">Check</button>';

    host.insertBefore(bar, host.firstChild);

    var input = bar.querySelector('.tps-plp-deliverybar__input');
    var btn = bar.querySelector('.tps-plp-deliverybar__btn');

    function apply() {
      var val = (input.value || '').replace(/\D/g, '').slice(0, 6);
      input.value = val;
      if (val.length !== 6) {
        input.setAttribute('aria-invalid', 'true');
        return;
      }
      input.removeAttribute('aria-invalid');
      /* Typed by the shopper, so it outranks auto-detection from now on. */
      savePin(val, 'user');
      resetRows();
      observeRows();
    }

    btn.addEventListener('click', apply);
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        apply();
      }
    });

    return bar;
  }

  async function initCollection() {
    var bar = buildBar();
    if (!bar) return;

    if (getPin().length !== 6) {
      /* First visit with no stored pincode: detect once, quietly. */
      await autoDetectPin();
      setBarState(bar);
    }

    if (getPin().length === 6) observeRows();

    /* Facet filtering swaps the grid out; re-attach afterwards. */
    var container = document.getElementById('ProductGridContainer');
    if (container && 'MutationObserver' in window) {
      var mo = new MutationObserver(function () {
        if (getPin().length === 6) observeRows();
      });
      mo.observe(container, { childList: true, subtree: true });
    }
  }

  function init() {
    /* Business-rule globals live in tpscustomjs.js (deferred, footer).
       Bail out quietly if it has not loaded. */
    if (typeof getNextWorkingDate !== 'function' || typeof filterCourierDays !== 'function') return;

    syncProductPage();
    initCollection();
  }

  /* ---------------- Shared API for other surfaces ----------------
     Lets other pages (e.g. the cart) reuse the exact same pincode memory
     and ETD logic — same wording, same business rules — without
     duplicating any of it. Set at load time; fetchEtd keeps its original
     closure, so it still resolves the tpscustomjs.js globals correctly
     when called from elsewhere. */
  window.tpsDelivery = {
    getPin: getPin,
    savePin: savePin,
    fetchEtd: fetchEtd,
    fmtDate: fmtDate,
    autoDetectPin: autoDetectPin
  };

  if (document.readyState === 'complete') init();
  else window.addEventListener('load', init);
})();